<script setup>
defineProps({
  name: {
    type: String,
    required: true
  },
  filled: Boolean
})
</script>

<template>
  <svg
    class="viewer-icon"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      v-if="name === 'heart'"
      d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"
      :fill="filled ? 'currentColor' : 'none'"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linecap="round"
      stroke-linejoin="round"
    />

    <template v-else-if="name === 'home'">
      <path d="m3.6 10.5 8.4-6.9 8.4 6.9" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M5.5 9.3v10.1h13V9.3M9.1 19.4v-6.1h5.8v6.1" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
    </template>

    <template v-else-if="name === 'info'">
      <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
      <path d="M12 10.7v5.5" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" />
      <circle cx="12" cy="7.5" r="1.05" fill="currentColor" />
    </template>

    <template v-else-if="name === 'ai'">
      <path d="M12 2.9 13.15 7a5.25 5.25 0 0 0 3.7 3.7L21 12l-4.15 1.3a5.25 5.25 0 0 0-3.7 3.7L12 21.1 10.85 17a5.25 5.25 0 0 0-3.7-3.7L3 12l4.15-1.3A5.25 5.25 0 0 0 10.85 7L12 2.9Z" stroke="currentColor" stroke-width="1.55" stroke-linejoin="round" />
      <path d="m18.8 3.4.35 1.25a1.6 1.6 0 0 0 1.1 1.1l1.25.35-1.25.35a1.6 1.6 0 0 0-1.1 1.1L18.8 8.8l-.35-1.25a1.6 1.6 0 0 0-1.1-1.1L16.1 6.1l1.25-.35a1.6 1.6 0 0 0 1.1-1.1l.35-1.25Z" fill="currentColor" />
    </template>

    <template v-else-if="name === 'user'">
      <circle cx="12" cy="8.1" r="3.25" stroke="currentColor" stroke-width="1.65" />
      <path d="M5.7 19.15c.45-3.25 2.72-5.1 6.3-5.1s5.85 1.85 6.3 5.1" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" />
    </template>

    <template v-else-if="name === 'annotation'">
      <path d="M5.1 4.2h13.8a2 2 0 0 1 2 2v8.9a2 2 0 0 1-2 2h-7.15L7.4 20.4v-3.3H5.1a2 2 0 0 1-2-2V6.2a2 2 0 0 1 2-2Z" stroke="currentColor" stroke-width="1.55" stroke-linejoin="round" />
      <circle cx="8" cy="10.6" r="1.1" fill="currentColor" />
      <circle cx="12" cy="10.6" r="1.1" fill="currentColor" />
      <circle cx="16" cy="10.6" r="1.1" fill="currentColor" />
    </template>

    <template v-else-if="name === 'eye' || name === 'eye-off'">
      <path d="M2.7 12s3.3-5.4 9.3-5.4 9.3 5.4 9.3 5.4-3.3 5.4-9.3 5.4S2.7 12 2.7 12Z" stroke="currentColor" stroke-width="1.55" stroke-linejoin="round" />
      <circle cx="12" cy="12" r="2.65" stroke="currentColor" stroke-width="1.55" />
      <path v-if="name === 'eye-off'" d="m4 3.8 16 16.4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
    </template>

    <template v-else-if="name === 'panels'">
      <rect x="3.1" y="4" width="17.8" height="6.2" rx="1.7" stroke="currentColor" stroke-width="1.55" />
      <rect x="3.1" y="13.8" width="17.8" height="6.2" rx="1.7" stroke="currentColor" stroke-width="1.55" />
    </template>

    <template v-else-if="name === 'callout'">
      <circle cx="4.5" cy="16.8" r="1.65" fill="currentColor" />
      <path d="m6.2 15.9 3.15-3.15h2.15" stroke="currentColor" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
      <rect x="11.5" y="5" width="9.4" height="10.1" rx="2" stroke="currentColor" stroke-width="1.55" />
      <path d="M14.3 8.4h3.8M14.3 11.4h2.7" stroke="currentColor" stroke-width="1.45" stroke-linecap="round" />
    </template>

    <template v-else-if="name === 'edit'">
      <path d="m4 20 4.1-.9L19.3 7.9a2.15 2.15 0 0 0-3.05-3.05L5.05 16.05 4 20Z" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round" />
      <path d="m14.9 6.2 3 3" stroke="currentColor" stroke-width="1.65" />
    </template>

    <template v-else-if="name === 'trash'">
      <path d="M4.5 6.5h15M9.2 3.5h5.6l.8 3H8.4l.8-3ZM7 6.5l.8 14h8.4l.8-14M10 10v6.5M14 10v6.5" stroke="currentColor" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
    </template>

    <template v-else-if="name === 'upload'">
      <path d="M12 15V3.8m0 0L7.8 8M12 3.8 16.2 8M4 14v4.2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V14" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round" />
    </template>

    <template v-else-if="name === 'download'">
      <path d="M12 3.8V15m0 0-4.2-4.2M12 15l4.2-4.2M4 14v4.2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V14" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round" />
    </template>

    <template v-else-if="name === 'drag-grip'">
      <circle cx="8" cy="6.5" r="1.35" fill="currentColor" />
      <circle cx="16" cy="6.5" r="1.35" fill="currentColor" />
      <circle cx="8" cy="12" r="1.35" fill="currentColor" />
      <circle cx="16" cy="12" r="1.35" fill="currentColor" />
      <circle cx="8" cy="17.5" r="1.35" fill="currentColor" />
      <circle cx="16" cy="17.5" r="1.35" fill="currentColor" />
    </template>

    <template v-else-if="name === 'help'">
      <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
      <path d="M9.75 9.15a2.46 2.46 0 0 1 2.51-2.22c1.47 0 2.55.9 2.55 2.24 0 1.23-.61 1.78-1.62 2.41-.91.57-1.19 1.03-1.19 2.02" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" />
      <circle cx="12" cy="16.75" r="1" fill="currentColor" />
    </template>

    <template v-else-if="name === 'sliders'">
      <path d="M4 7h5M15 7h5M4 17h9M19 17h1" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
      <circle cx="12" cy="7" r="2.35" stroke="currentColor" stroke-width="1.7" />
      <circle cx="16" cy="17" r="2.35" stroke="currentColor" stroke-width="1.7" />
    </template>

    <template v-else-if="name === 'settings'">
      <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.7" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06-2.87 2.87-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21h-4v-.1A1.7 1.7 0 0 0 8.6 19.4a1.7 1.7 0 0 0-1.87.34l-.06.06-2.87-2.87.06-.06A1.7 1.7 0 0 0 4.2 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H2.4v-4h.1A1.7 1.7 0 0 0 4.2 8.6a1.7 1.7 0 0 0-.34-1.87l-.06-.06L6.67 3.8l.06.06A1.7 1.7 0 0 0 8.6 4.2a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1v-.1h4v.1a1.7 1.7 0 0 0 1 1.7 1.7 1.7 0 0 0 1.87-.34l.06-.06 2.87 2.87-.06.06A1.7 1.7 0 0 0 19.4 8.6c.14.37.35.71.6 1 .29.29.68.43 1.1.4h.1v4h-.1a1.7 1.7 0 0 0-1.7 1Z" stroke="currentColor" stroke-width="1.45" stroke-linecap="round" stroke-linejoin="round" />
    </template>

    <template v-else-if="name === 'rotate'">
      <path d="M19.3 8.2A8 8 0 1 0 20 14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M19.5 3.8v4.8h-4.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
    </template>

    <template v-else-if="name === 'nodes'">
      <path d="m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8M7.1 7.1h9.8v9.8H7.1V7.1Z" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" />
      <circle cx="6.2" cy="6.2" r="2.2" fill="var(--icon-surface, #fff)" stroke="currentColor" stroke-width="1.55" />
      <circle cx="17.8" cy="6.2" r="2.2" fill="var(--icon-surface, #fff)" stroke="currentColor" stroke-width="1.55" />
      <circle cx="6.2" cy="17.8" r="2.2" fill="var(--icon-surface, #fff)" stroke="currentColor" stroke-width="1.55" />
      <circle cx="17.8" cy="17.8" r="2.2" fill="var(--icon-surface, #fff)" stroke="currentColor" stroke-width="1.55" />
    </template>

    <template v-else-if="name === 'camera'">
      <path d="M7.3 7.1 8.55 5h6.9l1.25 2.1H19a2 2 0 0 1 2 2v8.4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9.1a2 2 0 0 1 2-2h2.3Z" stroke="currentColor" stroke-width="1.65" stroke-linejoin="round" />
      <circle cx="12" cy="13.1" r="3.35" stroke="currentColor" stroke-width="1.65" />
      <circle cx="18.1" cy="9.8" r=".85" fill="currentColor" />
    </template>

    <template v-else-if="name === 'play'">
      <path d="m8.2 5.7 9.7 6.3-9.7 6.3V5.7Z" fill="currentColor" />
    </template>

    <template v-else-if="name === 'pause'">
      <rect x="7" y="5.5" width="3.5" height="13" rx="1" fill="currentColor" />
      <rect x="13.5" y="5.5" width="3.5" height="13" rx="1" fill="currentColor" />
    </template>

    <template v-else-if="name === 'stop'">
      <rect x="6.2" y="6.2" width="11.6" height="11.6" rx="1.7" fill="currentColor" />
    </template>

    <template v-else-if="name === 'ar'">
      <path d="M4.2 8V5.8A1.8 1.8 0 0 1 6 4h2.2M15.8 4H18a1.8 1.8 0 0 1 1.8 1.8V8M19.8 16v2.2A1.8 1.8 0 0 1 18 20h-2.2M8.2 20H6a1.8 1.8 0 0 1-1.8-1.8V16" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" />
      <path d="m12 7.1 4.1 2.25v4.7L12 16.4l-4.1-2.35v-4.7L12 7.1Z" stroke="currentColor" stroke-width="1.45" stroke-linejoin="round" />
      <path d="m8.15 9.5 3.85 2.2 3.85-2.2M12 11.7v4.35" stroke="currentColor" stroke-width="1.35" stroke-linejoin="round" />
    </template>

    <path
      v-else-if="name === 'chevron-left'"
      d="m15 5.5-6 6.5 6 6.5"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />

    <path
      v-else-if="name === 'chevron-right'"
      d="m9 5.5 6 6.5-6 6.5"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />

    <path
      v-else-if="name === 'chevron-up'"
      d="m5.5 15 6.5-6 6.5 6"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />

    <path
      v-else-if="name === 'chevron-down'"
      d="m5.5 9 6.5 6 6.5-6"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />

    <template v-else-if="name === 'image'">
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" stroke="currentColor" stroke-width="1.65" />
      <circle cx="8.1" cy="9.3" r="1.55" stroke="currentColor" stroke-width="1.45" />
      <path d="m5.8 17 4.1-4.1 2.7 2.5 2.45-2.35L18.5 17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
    </template>

    <template v-else-if="name === 'video'">
      <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" stroke-width="1.65" />
      <path d="m10 9 5.4 3-5.4 3V9Z" fill="currentColor" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round" />
    </template>

    <template v-else-if="name === 'video-record'">
      <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" stroke-width="1.65" />
      <circle cx="12" cy="12" r="3.25" fill="currentColor" />
    </template>

    <template v-else-if="name === 'audio'">
      <path d="M9.3 17.7V6.5l9-2.1v10.7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M9.3 8.7 18.3 6.6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
      <ellipse cx="6.8" cy="17.8" rx="2.5" ry="1.8" fill="currentColor" />
      <ellipse cx="15.8" cy="15.2" rx="2.5" ry="1.8" fill="currentColor" />
    </template>

    <template v-else-if="name === 'cube'">
      <path d="m12 2.8 8 4.55v9.3l-8 4.55-8-4.55v-9.3l8-4.55Z" stroke="currentColor" stroke-width="1.55" stroke-linejoin="round" />
      <path d="m4.4 7.55 7.6 4.3 7.6-4.3M12 11.85v8.8" stroke="currentColor" stroke-width="1.55" stroke-linejoin="round" />
    </template>

    <template v-else-if="name === 'gesture'">
      <path d="M12 3v18M3 12h18M12 3 9.5 5.5M12 3l2.5 2.5M21 12l-2.5-2.5M21 12l-2.5 2.5M12 21l-2.5-2.5M12 21l2.5-2.5M3 12l2.5-2.5M3 12l2.5 2.5" stroke="currentColor" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
    </template>

    <template v-else-if="name === 'hand-drag'">
      <path d="M9.15 11.65V5.8a1.68 1.68 0 0 1 3.35 0v5.05M12.5 9.55V8.2a1.58 1.58 0 0 1 3.15 0v3.1M15.65 10.15v-.8a1.5 1.5 0 0 1 3 0v3.25" stroke="currentColor" stroke-width="1.55" stroke-linecap="round" />
      <path d="m9.15 11.3-1.4-1.35a1.67 1.67 0 0 0-2.36 2.36l4.4 6.12A3.8 3.8 0 0 0 12.88 20h1.62a4.15 4.15 0 0 0 4.15-4.15V12.6" stroke="currentColor" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
    </template>

    <template v-else-if="name === 'pinch'">
      <path d="M9.35 9.35 5.1 5.1m0 0h3.75M5.1 5.1v3.75M14.65 14.65l4.25 4.25m0 0h-3.75m3.75 0v-3.75" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round" />
      <circle cx="11.15" cy="11.15" r="1.75" fill="currentColor" opacity=".22" stroke="currentColor" stroke-width="1.35" />
      <circle cx="12.85" cy="12.85" r="1.75" fill="var(--icon-surface, #fff)" stroke="currentColor" stroke-width="1.35" />
    </template>

    <template v-else-if="name === 'two-finger-pan'">
      <rect x="7.05" y="3.8" width="3.55" height="9.1" rx="1.78" stroke="currentColor" stroke-width="1.5" />
      <rect x="13.4" y="3.8" width="3.55" height="9.1" rx="1.78" stroke="currentColor" stroke-width="1.5" />
      <path d="M4 18.2h16M4 18.2l2.6-2.6M4 18.2l2.6 2.6M20 18.2l-2.6-2.6M20 18.2l-2.6 2.6" stroke="currentColor" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
    </template>

    <template v-else-if="name === 'double-tap'">
      <circle class="viewer-icon__tap-ring" cx="12" cy="6.2" r="3.6" stroke="currentColor" stroke-width="1.35" />
      <path d="M10.2 16.3V9.4a1.8 1.8 0 0 1 3.6 0v4.05l1.25-.85a1.55 1.55 0 0 1 2.25.56l1.25 2.38a3.1 3.1 0 0 1-.25 3.3A3.1 3.1 0 0 1 15.5 20h-1.85a4 4 0 0 1-3.45-2l-1.45-2.5a1.45 1.45 0 0 1 2.1-1.9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </template>

    <template v-else-if="name === 'joystick'">
      <circle cx="12" cy="12" r="8.4" stroke="currentColor" stroke-width="1.45" />
      <path d="M12 5.3v13.4M5.3 12h13.4" stroke="currentColor" stroke-width="1.05" stroke-linecap="round" opacity=".28" />
      <circle class="viewer-icon__joystick-thumb" cx="12" cy="12" r="3.15" fill="var(--icon-surface, #fff)" stroke="currentColor" stroke-width="1.55" />
    </template>

    <template v-else-if="name === 'reset'">
      <g transform="scale(.0234146341 .0234375)" fill="currentColor" stroke="currentColor" stroke-width="24" stroke-linecap="round" stroke-linejoin="round">
        <path d="M300.153441 264.670766L45.877599 355.126598c-14.792777 5.297413-31.084822-2.49878-36.382235-17.291557-5.297413-14.792777 2.49878-31.084822 17.291556-36.382235l254.275842-90.455832c14.792777-5.297413 31.084822 2.49878 36.382236 17.291557 5.297413 14.792777-2.49878 31.184773-17.291557 36.382235z" />
        <path d="M113.244705 67.966813L64.36857 333.437189c-2.798633 15.392484-17.791313 25.687457-33.183797 22.888824-15.392484-2.798633-25.687457-17.791313-22.888824-33.183797L57.172084 57.67184C59.970717 42.279356 74.963397 31.984383 90.355881 34.882967c15.392484 2.798633 25.687457 17.691362 22.888824 33.083846z" />
        <path d="M84.958516 744.636408m-24.987799 0a24.987799 24.987799 0 1 0 49.975598 0 24.987799 24.987799 0 1 0-49.975598 0Z" />
        <path d="M512.749634 0.499756C308.649292 0.499756 132.435334 120.041386 50.275451 292.857003h56.072621C184.409956 148.527477 337.135383 50.475354 512.749634 50.475354c255.075451 0 461.774524 206.699073 461.774524 461.774524s-206.699073 461.774524-461.774524 461.774524c-175.614251 0-328.339678-98.052123-406.401562-242.381649H68.966325v20.989751h-2.998536v2.998536h-3.39834c86.557735 159.821962 255.675159 268.36896 450.180185 268.36896 282.661981 0 511.750122-229.088141 511.750122-511.750122S795.411615 0.499756 512.749634 0.499756z" />
      </g>
    </template>

    <template v-else-if="name === 'mode-orbit'">
      <ellipse cx="12" cy="12" rx="8.5" ry="4.75" transform="rotate(-28 12 12)" stroke="currentColor" stroke-width="1.55" />
      <path d="M5.2 8.7 4.05 6.4 1.8 7.75M18.8 15.3l1.15 2.3 2.25-1.35" stroke="currentColor" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
      <circle cx="12" cy="12" r="2.35" fill="var(--icon-surface, #fff)" stroke="currentColor" stroke-width="1.55" />
    </template>

    <template v-else-if="name === 'mode-flight'">
      <path d="m20.9 3.1-7.55 17.8-2.25-7.95-8-2.25 17.8-7.6Z" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round" />
      <path d="m11.1 12.95 4.1-4.1" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" />
    </template>

    <template v-else-if="name === 'screen-landscape'">
      <rect x="2.8" y="5.3" width="18.4" height="13.4" rx="2.3" stroke="currentColor" stroke-width="1.65" />
      <path d="M9.4 16.1h5.2" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" />
    </template>

    <template v-else-if="name === 'screen-portrait'">
      <rect x="6.4" y="2.7" width="11.2" height="18.6" rx="2.3" stroke="currentColor" stroke-width="1.65" />
      <path d="M10.2 18.5h3.6" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" />
    </template>

    <template v-else-if="name === 'share'">
      <path
        transform="scale(.0234375)"
        d="M1009.777778 503.466667l-443.733334-455.111111c-5.688889-5.688889-11.377778 0-11.377777 5.688888v267.377778C8.533333 409.6 2.844444 918.755556 17.066667 932.977778c0 0 45.511111-48.355556 164.977777-113.777778 85.333333-48.355556 224.711111-85.333333 369.777778-102.4v261.688889c0 8.533333 11.377778 11.377778 14.222222 5.688889l443.733334-480.711111z"
        fill="currentColor"
      />
    </template>

    <template v-else-if="name === 'more'">
      <circle cx="5.5" cy="12" r="1.55" fill="currentColor" />
      <circle cx="12" cy="12" r="1.55" fill="currentColor" />
      <circle cx="18.5" cy="12" r="1.55" fill="currentColor" />
    </template>
  </svg>
</template>
